import { useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { LuShieldCheck, LuTriangleAlert, LuCircleX } from 'react-icons/lu'

import type { ReviewDimension, DimensionStatus } from '@/api/design-review'

interface ReviewScoreTableProps {
  dimensions: ReviewDimension[]
}

const STATUS_CONFIG: Record<
  DimensionStatus,
  { label: string; bg: string; color: string; icon: typeof LuShieldCheck }
> = {
  PASS: {
    label: 'Pass',
    bg: 'rgba(34, 197, 94, 0.1)',
    color: '#4ade80',
    icon: LuShieldCheck
  },
  WARNING: {
    label: 'Warning',
    bg: 'rgba(234, 179, 8, 0.1)',
    color: '#facc15',
    icon: LuTriangleAlert
  },
  FAIL: {
    label: 'Fail',
    bg: 'rgba(239, 68, 68, 0.1)',
    color: '#f87171',
    icon: LuCircleX
  },
  SKIPPED: {
    label: 'Skipped',
    bg: 'rgba(255, 255, 255, 0.04)',
    color: 'rgba(255, 255, 255, 0.4)',
    icon: LuShieldCheck
  }
}

function DimensionRow({ dim }: { dim: ReviewDimension }) {
  const [isOpen, setIsOpen] = useState(false)
  const config = STATUS_CONFIG[dim.status]
  const Icon = config.icon

  return (
    <Box
      px="10px"
      py="8px"
      borderRadius="10px"
      bg="rgba(255,255,255,0.03)"
      border="1px solid"
      borderColor="rgba(255,255,255,0.06)"
      cursor={dim.rationale ? 'pointer' : undefined}
      transition="background 0.2s ease"
      _hover={dim.rationale ? { bg: 'rgba(255,255,255,0.05)' } : undefined}
      onClick={() => dim.rationale && setIsOpen((v) => !v)}
    >
      <Flex align="center" justify="space-between">
        <Text textStyle="bodySuperSmall" color="whiteAlpha.800">
          {dim.label}
        </Text>
        <Flex
          align="center"
          gap="4px"
          px="8px"
          py="2px"
          borderRadius="full"
          bg={config.bg}
        >
          <Icon size={11} color={config.color} />
          <Text
            textStyle="bodySuperSmall"
            color={config.color}
            fontWeight={500}
          >
            {config.label}
          </Text>
        </Flex>
      </Flex>
      <AnimatePresence initial={false}>
        {isOpen && dim.rationale && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <Text
              textStyle="caption"
              color="whiteAlpha.500"
              lineHeight="1.4"
              mt="6px"
            >
              {dim.rationale}
            </Text>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

export default function ReviewScoreTable({
  dimensions
}: ReviewScoreTableProps) {
  return (
    <Flex flexDirection="column" gap="6px">
      {dimensions.map((dim) => (
        <DimensionRow key={dim.name} dim={dim} />
      ))}
    </Flex>
  )
}
